import React from 'react'
import { Link } from 'react-router-dom'
import { Row, Col, Container, Card, Button, Badge, ListGroup } from 'react-bootstrap'

import Meta from '../components/Meta'

const theme = {
  gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 45%, #db2777 100%)',
  softGradient: 'linear-gradient(135deg, rgba(79,70,229,0.12) 0%, #eed8f4ff 50%, #f3ebf5 100%)',
  cardShadow: '0 24px 70px rgba(31, 41, 55, 0.14)',
  border: '1px solid rgba(99, 102, 241, 0.16)'
}

const urls = {
  doqlets:  '/doqlets',
  doqletsGithub: 'https://github.com/pixagan/doqlets',
  ragLearn: 'https://youtu.be/z-lXaIow93A?si=cFSI_PXk6uVT4j3h'
}

const doqletsCapabilities = [
    {
    title: 'LLM-managed Smart Blocks',
    text: 'Relevant information is automatically added to relevant Blocks by Agentic Workflow',
    items: ['Auto-generated Wiki pages', 'Knowledge summaries', 'Continuously organized content']
  },
  {
    title: 'Document ingestion',
    text: 'Add docs and knowledge sources that are prepared for retrieval automatically.',
    items: ['Upload and organize documents', 'Automatic chunking', 'Metadata-aware storage']
  },
  {
    title: 'Vector store retrieval',
    text: 'Chunks are stored in a vector store so users and apps can search semantically.',
    items: ['RAG-ready embeddings', 'Semantic search', 'Context retrieval for LLMs']
  },
  
  {
    title: 'Knowledge graph roadmap',
    text: 'Graph Store integration is under development to connect entities, topics and relationships.',
    items: ['Entity extraction', 'Relationship mapping', 'Knowledge graph foundation']
  },
  {
    title: 'Agentic RAG',
    text: 'Ask questions across stored knowledge and receive answers grounded in your documents.',
    items: ['Doc chat', 'Source-aware answers', 'Conversational knowledge access']
  },
  {
    title: 'Agents and skills',
    text: 'Add skills and run agents that use your knowledge store to complete tasks and workflows.',
    items: ['Task agents', 'Custom skills', 'Agentic workflows']
  }
]

const workflowSteps = [
  'Users add documents and knowledge sources.',
  'Doqlets chunks, embeds and stores the content with a vector store creating a Knowledge Base',
  'Users can create pages with AI managed Smart Blocks, defining what the blocks are meant to showcase',
  'Agentic workflows automatically update relevant smart blocks with relevant information.',
  'Users chat with the data or run agents with added skills.'
]

const gettingStartedSteps = [
  {
    title: 'Doqlets on GitHub',
    items: [
      `Doqlets is available at ${urls.doqletsGithub}`,
      'Open the repository to review the source code and setup notes.'
    ]
  },
  {
    title: 'Clone the repo',
    items: [
      'Clone the Doqlets repository to your local machine.',
      'Use Git or download the repository as a ZIP file.'
    ]
  },
  {
    title: 'Repo structure',
    items: [
      'The repo contains the UI/frontend.',
      'It also contains the core backend/API.'
    ]
  },
  {
    title: 'Install frontend and backend',
    items: [
      'Install dependencies for the UI/frontend.',
      'Install dependencies for the core backend/API.',
      'Run each service according to the repository instructions.'
    ]
  },
  {
    title: 'Follow tutorials',
    items: [
      'Use the tutorials section for detailed setup guidance.',
      'Follow the tutorials to get Doqlets running end-to-end.'
    ]
  }
]

const CTAButton = ({ href, variant = 'primary', children }) => {
  const isExternal = /^https?:\/\//.test(href)

  if (isExternal) {
    return (
      <Button href={href} target='_blank' rel='noreferrer' variant={variant} className='rounded-pill px-4 fw-semibold'>
        {children}
      </Button>
    )
  }

  return (
    <Button as={Link} to={href} variant={variant} className='rounded-pill px-4 fw-semibold'>
      {children}
    </Button>
  )
}

const Section = ({ id, eyebrow, title, subtitle, children }) => (
  <section id={id} className='py-5' style={{background: '#f3ebf5', marginBottom:'solid', marginBottom:'1px'}}>
    <Container>
      <div className='text-center mx-auto mb-5' style={{ maxWidth: 820 }}>
        {eyebrow && <Badge bg='light' text='primary' className='rounded-pill px-3 py-2 mb-3'>{eyebrow}</Badge>}
        <h2 className='fw-bold mb-3' style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: '#111827' }}>{title}</h2>
        {subtitle && <p className='lead mb-0' style={{ color: '#4b5563' }}>{subtitle}</p>}
      </div>
      {children}
    </Container>
  </section>
)

const InfoCard = ({ title, text, items }) => (
  <Card style={{ height: '100%', border: theme.border, borderRadius: 24, boxShadow: '0 16px 40px rgba(17, 24, 39, 0.08)' }}>
    <Card.Body className='p-4'>
      <h4 className='fw-bold mb-3' style={{ color: '#111827' }}>{title}</h4>
      <p style={{ color: '#4b5563', lineHeight: 1.7 }}>{text}</p>
      <ListGroup variant='flush' className='mt-3'>
        {items.map((item) => (
          <ListGroup.Item key={item} className='px-0 py-2 border-0' style={{ color: '#374151', background: 'transparent' }}>
            <span className='me-2' style={{ color: '#7c3aed' }}>●</span>{item}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </Card.Body>
  </Card>
)

const DoqletsScreen = () => {
  return (
    <div style={{ minHeight: '90vh', background: 'wh' }}>
      <Meta
        title='Doqlets - Open Source LLM Powered Knowledge Store'
        description='Doqlets is an open source LLM powered knowledge store with automated LLM managed Wiki, vector store retrieval, doc chat, agents, skills and upcoming graph store integration.'
      />

      <section style={{ background: theme.softGradient, padding: '88px 0 56px', marginBottom:'1px' }}>
        <Container>
          <Row className='align-items-center g-5'>
            <Col lg={7}>
              <Badge bg='light' className='rounded-pill px-3 py-2 mb-4'>Open Source Knowledge Technology</Badge>
              <h1 className='fw-bold mb-4' style={{ fontFamily:'Geist',fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 1.05, color: '#111827' }}>
                From Knowlegde to AI Managed Intelligence
              </h1>
              <p className='lead mb-4' style={{ color: '#4b5563', lineHeight: 1.8 }}>
                Add documents, and information from different sources, let Agents help you update Smart Blocks or answer queries and generate slides, reports or more from the Knowledge store.
              </p>
              <div className='d-flex gap-3 flex-wrap'>
                <CTAButton href={urls.doqlets}>Explore Doqlets</CTAButton>
                <CTAButton href={urls.doqletsGithub} variant='outline-primary'>View on GitHub</CTAButton>
                <CTAButton href={urls.ragLearn} variant='outline-dark'>Learn About RAG</CTAButton>
              </div>
            </Col>
            <Col lg={5}>
              <Card style={{ border: 'none', borderRadius: 32, background: theme.gradient, color: '#fff', boxShadow: theme.cardShadow }}>
                <Card.Body className='p-4 p-md-5'>
                  <h3 className='fw-bold mb-3' style={{ color: 'white' }}>Doqlets is built on Laeyerz.</h3>
                  <p style={{ color: '#f1efff', fontSize: 18, lineHeight: 1.75 }}>
                    Doqlets turns collections of documents and knowledge sources into searchable, conversational and AI-managed knowledge systems.
                  </p>
                  <p style={{ color: '#d8d5ff', lineHeight: 1.7 }}>
                    The same knowledge system can power RAG applications, AI agents, agentic workflows and knowledge assistants.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <Section
        id='doqlets'
        eyebrow='Knowledge technology'
        title='What Doqlets provides'
        subtitle='Open source RAG infrastructure, automated knowledge organization, doc chat and agent capabilities in one knowledge platform.'
      >
        <Row className='g-4'>
          {doqletsCapabilities.map((capability) => (
            <Col md={6} lg={4} key={capability.title}>
              <InfoCard title={capability.title} text={capability.text} items={capability.items} />
            </Col>
          ))}
        </Row>
      </Section>

      <Section
        id='gettingstarted'
        eyebrow='Install and Go'
        title='Getting started with Doqlets'
        subtitle='The Open Source Version of Doqlets is available on Github. Download/Clone and install to get started'
      >
        <Card style={{ border: theme.border, borderRadius: 28, boxShadow: theme.cardShadow }}>
          <Card.Body className='p-4 p-md-5'>
  <div
    className='d-flex gap-4 overflow-auto pb-3'
    style={{ scrollSnapType: 'x mandatory' }}
  >
    {gettingStartedSteps.map((step, index) => (
      <Card
        key={step.title}
        style={{
          minWidth: 300,
          maxWidth: 340,
          border: theme.border,
          borderRadius: 24,
          background: '#f8f7ff',
          scrollSnapAlign: 'start'
        }}
      >
        <Card.Body className='p-4'>
          <div
            className='d-flex align-items-center justify-content-center mb-3'
            style={{
              width: 42,
              height: 42,
              borderRadius: 999,
              background: '#4f46e5',
              color: '#fff',
              fontWeight: 700
            }}
          >
            {index + 1}
          </div>

          <h4 className='fw-bold mb-3' style={{ color: '#111827' }}>
            {step.title}
          </h4>

          <ListGroup variant='flush'>
            {step.items.map((item) => (
              <ListGroup.Item
                key={item}
                className='px-0 py-2 border-0'
                style={{ color: '#374151', background: 'transparent' }}
              >
                <span className='me-2' style={{ color: '#7c3aed' }}>●</span>
                {item}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    ))}
  </div>
</Card.Body>
        </Card>
      </Section>

      <Section
        id='workflow'
        eyebrow='How it works'
        title='From documents to managed knowledge'
        subtitle='Doqlets creates a workflow for storing, retrieving, organizing and using knowledge with LLMs.'
      >
        <Card style={{ border: theme.border, borderRadius: 28, boxShadow: theme.cardShadow }}>
          <Card.Body className='p-4 p-md-5'>
            <Row className='g-4'>
              {workflowSteps.map((step, index) => (
                <Col md={6} lg={index === workflowSteps.length - 1 ? 12 : 6} key={step}>
                  <div className='d-flex align-items-start gap-3 p-3 rounded-4' style={{ background: '#f8f7ff', height: '100%' }}>
                    <div className='d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: 42, height: 42, borderRadius: 999, background: '#4f46e5', color: '#fff', fontWeight: 700 }}>
                      {index + 1}
                    </div>
                    <p className='mb-0' style={{ color: '#374151', lineHeight: 1.7 }}>{step}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      </Section>
    </div>
  )
}

export default DoqletsScreen
